import { computed, makeObservable } from 'mobx'
import { RRule, rrulestr } from 'rrule'

import i18n from 'modules/core/lib/i18n'
import { ModelWithPolicy } from 'modules/core/lib/modelWithPolicy'
import SurveysFormPolicy, {
  SurveysFormVerbTypes,
} from 'modules/core/store/models/policies/SurveysFormPolicy'

export const Status = {
  draft: 'draft',
  active: 'active',
  finished: 'finished',
}

// FIXME: false positive of enum members not being used
export const enum FormType {
  Survey = 'survey',
  Enps = 'enps',
}

const Types = {
  survey: FormType.Survey,
  enps: FormType.Enps,
}

interface Respondents {
  [key: string]: number | null | string | number[] | undefined
  access_ids: number[]
}

class Form extends ModelWithPolicy<any, SurveysFormVerbTypes> {
  static Types = Types

  constructor(attr = {}) {
    super(attr)
    makeObservable(this, {
      isDraft: computed,
      isActive: computed,
      isFinished: computed,
      isAnonymous: computed,
      periodicity: computed,
      hasSchedule: computed,
    })
  }

  get indexes() {
    return ['access_id', 'surveys_form_id']
  }

  static urlRoot() {
    return '/surveys/forms'
  }

  get policy() {
    return new SurveysFormPolicy(this)
  }

  get isDraft(): boolean {
    return this.attributes.state === Status.draft
  }

  get isActive(): boolean {
    return this.attributes.state === Status.active
  }

  get isFinished(): boolean {
    return this.attributes.state === Status.finished
  }

  get isAnonymous(): boolean {
    return this.attributes.anonymous
  }

  get answeredQuestionsCount(): number {
    return this.attributes.answered_question_groups_count
  }

  get isEnps(): boolean {
    return this.attributes.type === Types.enps
  }

  get periodicity(): string {
    if (this.attributes.schedule) {
      const periodicity = rrulestr(this.attributes.schedule, {
        cache: true,
      }).options

      switch (periodicity.freq) {
        case RRule.DAILY:
          return i18n.t('surveys.form.periodicity.daily')

        case RRule.YEARLY:
          return i18n.t('surveys.form.periodicity.yearly')

        case RRule.MONTHLY:
          switch (periodicity.interval) {
            case 1:
              return i18n.t('surveys.form.periodicity.monthly')
            case 2:
              return i18n.t('surveys.form.periodicity.bi_monthly')
            case 3:
              return i18n.t('surveys.form.periodicity.quarterly')
            case 4:
              return i18n.t('surveys.form.periodicity.quadrimester')
            case 6:
              return i18n.t('surveys.form.periodicity.bi_annual')
          }

          break

        case RRule.WEEKLY:
          return i18n.t('surveys.form.periodicity.weekly')
      }
    } else if (this.attributes.start_on) {
      return i18n.t('surveys.form.periodicity.one_time.survey')
    }

    return '-'
  }

  get hasSchedule(): boolean {
    return !!this.attributes.schedule
  }

  fetchRespondents(): Promise<Respondents> {
    return this.rpc('respondents')
  }

  remindRespondents(): Promise<unknown> {
    return this.rpc('remind_respondents')
  }
}

export default Form
